import { observer } from 'mobx-react';
import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import toast from 'react-hot-toast';
import Header from '../../shared-components/header/header';
import Grid from '../../shared-components/Grid/Grid';
import { CustomActionFormatter } from '../../shared-components/Grid/GridFormatter';
import Pagination from '../../shared-components/Grid/Pagination';
import DeletePopup from '../../shared-components/popup/delete/delete-popup';
import DeletePopupHOC from '../../shared-components/popup/delete/delete-popup-hoc';
import {
  DeleteIcon,
  EditIcon,
  PlusIcon,
  RefreshIcon,
  FilterIcon,
} from '../../shared-components/svgs/svgs';
import { formatMessage } from '../../translations/format-message';
import './check-item-template.css';
import { Search } from 'react-bootstrap-icons';
import { ModuleType } from '../../constants/enums/module-type-enum';
import userSettingEnum from '../../constants/enums/user-setting-enum';
import {
  isOperationPermittedRoleBase,
  showIconsRoleBase,
} from '../../helpers/action-permission-helper';
import AddEditCheckItemTemplate from './components/add-edit-check-item-template';
import { useStore } from '../../contexts/store-provider';
import { getTenantIdFromLocalStorage } from '../../helpers/localstorage-helper';
import FilterComponent from './components/filter';
import { ICheckItemFilter } from '../../models/ICheckItemFilter';
import {
  CheckItemDefaultSortColumnOrder,
  CheckItemFilterInitialState,
} from '../../core/initialState/filter-initial-state';
import IDefaultColumSortOrder from '../../models/IDefaultColumSortOrder';
import FilterPopupHOC from '../../shared-components/hoc/filter-popup-hoc/filter-popup-hoc';
import FormLoader from '../../shared-components/FormLoader/FormLoader';
import SlidingPanel from 'react-sliding-side-panel';
import AddCheckItemTemplatePopupHOC from './hoc/add-check-item-template-popup-hoc';
import CheckItemTemplateDetails from './check-item-template-details';
import { GridRemoteConstant } from '../../constants/grid-constant';

const CheckItemTemplate = (props: any) => {
  const { preferencesStore, checkItemTemplateStore, processStore } = useStore();
  const {
    userSetting,
    setUserSetting,
    language,
    islanguageChanged,
    updateLanguageChangedFlag,
  } = preferencesStore;
  const {
    checkItemTemplateList,
    GetCheckItemTemplateListService,
    getAllCheckItemTemplateState,
    DeleteCheckItemTemplateService,
    resetDeleteState,
    deleteCheckItemTemplateState,
  } = checkItemTemplateStore;
  const isFirstLoad = useRef(true);
  const { allProcessOptions, GetAllProcessService } = processStore;

  const [searchTerm, setSearchTerm] = useState<ICheckItemFilter>(
    CheckItemFilterInitialState
  );
  const [pageLimit, setPageLimit] = useState(userSetting.pageLimitConfig);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedId, setSelectedId] = useState(-1);
  const [selectedName, setSelectedName] = useState('');
  const [filterObj, setFilterObj] = useState<ICheckItemFilter>(
    CheckItemFilterInitialState
  );
  const [columSortOrder, setColumnSortOrder] = useState<IDefaultColumSortOrder>(
    CheckItemDefaultSortColumnOrder
  );

  const [isFilterCleared, setIsFilterCleared] = useState(true);
  const [selectedRow, setSelectedRow] = useState<any>({});
  const [openPanel, setOpenPanel] = useState(false);
  const [isFilterApplied, setIsFilterApplied] = useState<boolean>(false);

  const tenantId = Number(getTenantIdFromLocalStorage());

  useEffect(() => {
    GetAllProcessService();
  }, []);

  useEffect(() => {
    closePanelhandler();
    getCheckItemListServiceHandler();
  }, [currentPage, pageLimit, tenantId, language]);

  useEffect(() => {
    if (deleteCheckItemTemplateState.success) {
      toast.success(formatMessage('deleted_success'));
      props.deletePopupToggleHandler();
      handleRefresh();
      resetDeleteState();
    }
  }, [deleteCheckItemTemplateState.success]);

  useEffect(() => {
    if (checkItemTemplateStore.addUpdateCheckItemTemplateState.success) {
      props.addToggleHandler();
      handleRefresh();
      checkItemTemplateStore.resetAddUpdateState();
    }
  }, [checkItemTemplateStore.addUpdateCheckItemTemplateState.success]);

  useEffect(() => {
    if (islanguageChanged) {
      updateLanguageChangedFlag();
    }
  }, [islanguageChanged]);

  const getCheckItemListServiceHandler = (
    CheckItemFilter?: ICheckItemFilter,
    filterApplied?: boolean,
  ) => {
    if (!filterApplied) {
      CheckItemFilter = {
        ...filterObj,
        Column: columSortOrder.dataField,
        Order: columSortOrder.order,
      };
    }
    GetCheckItemTemplateListService(
      tenantId,
      filterApplied ? 1 : currentPage,
      pageLimit,
      CheckItemFilter
    );
  };

  const handleRefresh = () => {
    setCurrentPage(1);
    getCheckItemListServiceHandler();
  };

  const handleSearch = () => {
    setCurrentPage(1);
    getCheckItemListServiceHandler();
  };

  const openAddModal = () => {
    setSelectedId(-1);
    props.addToggleHandler();
  };

  const handleEdit = (row: any, event: any) => {
    setSelectedId(row.Id);
    event.stopPropagation();
    props.addToggleHandler();
  };

  const handleDeleteClick = (row: any, event: any) => {
    setSelectedId(row.Id);
    setSelectedName(row.CheckItemName);
    event.stopPropagation();
    props.deletePopupToggleHandler();
  };

  const deleteHandler = () => {
    DeleteCheckItemTemplateService(tenantId, selectedId);
  };

  const closeModal = () => {
    props.addToggleHandler();
    if (checkItemTemplateStore.addUpdateCheckItemTemplateState.success) {
      handleRefresh();
      checkItemTemplateStore.resetAddUpdateState();
    }
  };

  const actionIcons: any[] = showIconsRoleBase(
    [
      {
        dataField: 'isEdit',
        handler: handleEdit,
        icon: <EditIcon />,
        isDisable: !isOperationPermittedRoleBase(
          ModuleType.CheckItemTemplate,
          'Edit'
        ),
        title: 'edit',
        varient: 'primary',
        action: 'edit',
      },
      {
        dataField: 'isDelete',
        handler: handleDeleteClick,
        icon: <DeleteIcon />,
        isDisable: !isOperationPermittedRoleBase(
          ModuleType.CheckItemTemplate,
          'Delete'
        ),
        title: 'delete',
        varient: 'danger',
        action: 'delete',
      },
    ],
    ModuleType.CheckItemTemplate
  );

  const columns: any = [
    {
      dataField: 'serial_number',
      text: formatMessage('serial_number'),
      classes: 'show-Elipses',
      formatter: (_: any, __: any, rowIndex: number) => rowIndex + 1,
    },
    {
      dataField: 'CheckItemName',
      text: formatMessage('template_name'),
      classes: 'show-Elipses',
    },
    {
      dataField: 'ProcessName',
      text: formatMessage('process'),
      classes: 'show-Elipses',
      sort: true,
    },
    {
      dataField: '',
      formatter: CustomActionFormatter(actionIcons),
      text: formatMessage('actions'),
      classes: 'last-column',
      hidden: actionIcons.length > 0 ? false : true,
    },
  ];

  const rowEvents = (e: any, row: any, rowIndex: number) => {
    setSelectedRow(row);
    if (selectedRow.Id === undefined || selectedRow.Id !== row.Id) {
      setOpenPanel(true);
    } else {
      setOpenPanel(!openPanel);
    }
  };

  function rowClassFormat(row: any, rowId: number) {
    let className: string = '';
    if (selectedRow?.Id && row.Id === selectedRow.Id) {
      className = 'bg-row SelectedRow';
    }
    return className;
  }

  const onPageLimitChanged = (evt: any) => {
    setPageLimit(parseInt(evt.target.value));
    setCurrentPage(1);
    setUserSetting(userSettingEnum.UserLimitConfig, parseInt(evt.target.value));
  };

  const onPageChanged = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const createQueryString = (filter: ICheckItemFilter): string => {
    return Object.entries(filter)
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join('#');
  };

  const filterHandler = (object: ICheckItemFilter) => {
    const filters = {
      ...object,
      Column: columSortOrder.dataField,
      Order: columSortOrder.order,
    };
    const filter = createQueryString({ ...filters });
    getCheckItemListServiceHandler(filters, true);
    setFilterObj({ ...filters });
    props.filterPopupToggleHandler();
    closePanelhandler();
    setIsFilterCleared(false);
  };

  const filterParams = (obj: ICheckItemFilter, isfilterCleard: boolean) => {
    const filter = createQueryString({
      ...obj,
      Column: columSortOrder.dataField,
      Order: columSortOrder.order,
    });

    if (isfilterCleard) {
      setFilterObj({ ...obj });
      getCheckItemListServiceHandler(obj, true);
      closePanelhandler();
      setIsFilterCleared(true);
      setCurrentPage(1)
    } else {
      setFilterObj({ ...obj });
    }
    props.filterPopupToggleHandler();
  };

  const closePanelhandler = () => {
    setSelectedRow({});
    setOpenPanel(false);
  };

  const handleTableChange = (type: any, newState: any) => {
    if (isFirstLoad.current) {
      isFirstLoad.current = false;
      return;
    }

    setColumnSortOrder((prevState) => ({
      ...prevState,
      dataField: newState.sortField,
      order: newState.sortOrder,
    }));

    const filter = {...filterObj, Column: newState.sortField, Order: newState.sortOrder}
    getCheckItemListServiceHandler(filter);
  };

  // useEffect(() => {
  //     const filter = {
  //       ...filterObj,
  //       Column: columSortOrder.dataField,
  //       Order: columSortOrder.order,
  //     };

  //     getCheckItemListServiceHandler(filter);
  //   }, [columSortOrder]);

  return (
    <>
      {props.showAddCheckItemTemplatePopup && (
        <AddEditCheckItemTemplate
          id={selectedId}
          modalClosed={closeModal}
        />
      )}
      {props.showDeletePopup && (
        <DeletePopup
          title="delete_check_item_template"
          modalSubmit={deleteHandler}
          modalClosed={props.deletePopupToggleHandler}
          message="message_delete_check_item_template_confirm"
          isLoading={deleteCheckItemTemplateState.inProgress}
        />
      )}
      <div className="icon-nav navbar-fixed-top device-nav-bar userTopNav">
        <div className="row align-items-center">
          <Header
            className="col-lg-5 col-md-6 col-sm-6"
            label="check_item_template"
          />
          <div className="col-lg-7 col-md-6 col-sm-6">
            <ul className="add-list">
              <li>
                <FilterComponent
                  initialValues={filterObj}
                  id={0}
                  submitHandler={filterHandler}
                  filterParmas={filterParams}
                  onModalClosed={props.filterPopupToggleHandler}
                  isFilterCleared={isFilterCleared}
                  isLoading={false}
                  isDisable={false}
                  isFilterApplied={isFilterApplied}
                  setIsFilterApplied={setIsFilterApplied}
                  processOptions={allProcessOptions}
                />
              </li>
              <li title={formatMessage('refresh')}>
                <a
                  className="btn btn-light"
                  onClick={handleRefresh}
                >
                  <RefreshIcon />
                </a>
              </li>
              {isOperationPermittedRoleBase(
                ModuleType.CheckItemTemplate,
                'Add'
              ) && (
                <li title={formatMessage('add_check_item')}>
                  <a
                    className="btn btn-secondary"
                    onClick={openAddModal}
                  >
                    <span className="me-1">
                      <PlusIcon />
                    </span>
                    <FormattedMessage id="add_check_item" />
                  </a>
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>
      <div className={`${openPanel ? 'panelOpen' : ''}`}>
        <div className="card card-page">
          {(getAllCheckItemTemplateState.inProgress ||
            deleteCheckItemTemplateState.inProgress ||
            checkItemTemplateStore.addUpdateCheckItemTemplateState
              .inProgress) && (
            <FormLoader
              loading={
                getAllCheckItemTemplateState.inProgress ||
                deleteCheckItemTemplateState.inProgress ||
                checkItemTemplateStore.addUpdateCheckItemTemplateState
                  .inProgress
              }
            />
          )}
          <div className="tableDesign row-eq-height tableDevices p-3 changingPanel">
            <div className="table-responsive">
              <Grid
                data={checkItemTemplateList.CheckItems}
                columns={columns}
                keyField="Id"
                loading={getAllCheckItemTemplateState.inProgress}
                rowClasses={rowClassFormat}
                rowEvents={{ onClick: rowEvents }}
                hover={true}
                sort={{ ...columSortOrder }}
                remote={{ ...GridRemoteConstant }}
                onTableChange={handleTableChange}
              />
            </div>
          </div>
          <div className="row no-gutters p-3 pt-0 tableFooter">
            <div className="col-lg-3 col-md-6 col-sm-6 pt-1">
              <FormattedMessage id="show" />
              <select
                className="pageLimit"
                onChange={onPageLimitChanged}
                value={pageLimit}
              >
                {[10, 20, 50, 100].map((op) => (
                  <option
                    key={op}
                    value={op}
                  >
                    {op}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-lg-9 col-md-6 col-sm-6 d-flex justify-content-sm-end">
              <Pagination
                isRefreshPage={false}
                totalPages={Math.ceil(
                  (checkItemTemplateList?.PagingDetails?.TotalRecords || 0) /
                    pageLimit
                )}
                pageLimit={pageLimit}
                onPageChanged={onPageChanged}
                disablePagination={false}
                setIsRefreshPage={() => {}}
                currentPage={currentPage}
                totalRecords={
                  checkItemTemplateList?.PagingDetails?.TotalRecords || 0
                }
              />
            </div>
          </div>
        </div>
      </div>
      <SlidingPanel
        type={'right'}
        isOpen={openPanel}
        noBackdrop={false}
        size={100}
      >
        <div className="bg_slide panel">
          <div className="row bb p-2 pl-4 g-0 align-items-center ">
            <div className="col-lg-10 col-md-10 col-sm-10 col-xs-10">
              <h2 className="fw-bold mb-0 mt-1 ms-2 py-2">
                {selectedRow.CheckItemName}
              </h2>
            </div>
            <div className="col-lg-2 col-md-2 col-sm-2 col-xs-2 close-icon-detail">
              <span title={formatMessage('close')}>
                <svg
                  onClick={closePanelhandler}
                  role="button"
                  xlinkTitle="close"
                  xmlns="http://www.w3.org/2000/svg"
                  width="30"
                  height="30"
                  fill="currentColor"
                  className="bi bi-x ms-auto d-block"
                  viewBox="0 0 16 16"
                >
                  <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
                </svg>
              </span>
            </div>
          </div>
          <div className="group_detailsWrapper pb-3">
            <CheckItemTemplateDetails selectedRowId={selectedRow.Id} />
          </div>
        </div>
      </SlidingPanel>
    </>
  );
};

export default FilterPopupHOC(
  AddCheckItemTemplatePopupHOC(DeletePopupHOC(observer(CheckItemTemplate)))
);
